import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus,
  BookOpen,
  Library,
  PenLine,
  CheckCircle2,
  FileText,
  Sparkles,
  Users,
} from 'lucide-react';
import { useAppStore } from '../stores/app-store';
import { useSubscriptionStore, PLAN_NAMES } from '../stores/subscription-store';
import { countWords } from '../utils/word-count';
import { TemplateModal } from './template-modal';
import { BookMetadataModal } from './book-metadata-modal';
import { CoverStudio } from './cover-studio';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { EmptyState } from './ui/empty-state';
import { cn } from '../lib/utils';
import { notify, toast } from '../lib/toast';
import { BookCover } from './book-cover';
import { BookDetailDrawer } from './book-detail-drawer';
import { BookshelfView } from './bookshelf-view';
import { EstanteSkeleton } from './ui/skeletons';
import { ShareModal } from './share-modal';
import { GuidedCreateModal } from './guided-create-modal';
import { MangaCreateWizard } from './manga-create-wizard';
import { PageHeader } from './ui/page-header';
import type { Project } from '../types';

// ─── helpers ──────────────────────────────────────────────────────────────────

function deriveProgress(chapters: { content: string }[]): number {
  const total = chapters.reduce((s, ch) => s + countWords(ch.content), 0);
  return Math.min(100, Math.round((total / 80000) * 100));
}

/**
 * Returns a deterministic height in px (200–244) for a book based on its id.
 * Simulates real-world "trim size" variation across books on a shelf.
 */
function deriveHeight(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = seed.charCodeAt(i) + ((h << 5) - h);
  }
  const normalized = (Math.abs(h) % 1000) / 1000; // 0..0.999
  return Math.round(200 + normalized * 44); // 200–244 px
}

// ─── types ────────────────────────────────────────────────────────────────────

type ShelfCategory = 'writing' | 'drafts' | 'done';

interface ShelfDef {
  id: ShelfCategory;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  projects: Project[];
}

// ─── ProjectsView ─────────────────────────────────────────────────────────────

export function ProjectsView() {
  const { t } = useTranslation();
  const { projects, deleteProject, duplicateProject, updateProject, projectsLoaded } = useAppStore();
  const navigate = useNavigate();

  const { canCreateProject, usage, plan, fetchUsage } = useSubscriptionStore();
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [coverStudioProjectId, setCoverStudioProjectId] = useState<string | null>(null);


  // Drawer state
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [metaModalProject, setMetaModalProject] = useState<Project | null>(null);

  // Share modal state
  const [shareProjectId, setShareProjectId] = useState<string | null>(null);

  // Guided "create with AI" modal/drawer state
  const [guidedOpen, setGuidedOpen] = useState(false);

  // Manga/comic creation wizard state
  const [mangaWizardOpen, setMangaWizardOpen] = useState(false);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const canCreate = canCreateProject();

  const handleNewProject = () => {
    if (!canCreate) {
      notify.limitReached(t('projectsView.projectsFeatureName', 'projetos'), PLAN_NAMES[plan]);
      return;
    }
    setShowTemplateModal(true);
  };

  const handleGuidedCreate = () => {
    if (!canCreate) {
      notify.limitReached(t('projectsView.projectsFeatureName', 'projetos'), PLAN_NAMES[plan]);
      return;
    }
    setGuidedOpen(true);
  };

  const handleCreateManga = () => {
    if (!canCreate) {
      notify.limitReached(t('projectsView.projectsFeatureName', 'projetos'), PLAN_NAMES[plan]);
      return;
    }
    setMangaWizardOpen(true);
  };

  const handleDeleteProject = (id: string) => {
    const project = projects.find((p) => p.id === id);
    const title = project?.metadata.title || t('projects.untitled');

    toast(t('projects.deleteConfirm'), {
      description: title,
      action: {
        label: t('common.delete'),
        onClick: async () => {
          try {
            await deleteProject(id);
            notify.deleted(title);
            fetchUsage();
          } catch {
            notify.error(t('errors.saveFailed'));
          }
        },
      },
      cancel: {
        label: t('common.cancel'),
        onClick: () => {},
      },
      duration: 10000,
    });
  };

  const handleDuplicateProject = async (id: string) => {
    if (!canCreate) {
      notify.limitReached(t('projectsView.projectsFeatureName', 'projetos'), PLAN_NAMES[plan]);
      return;
    }
    try {
      await duplicateProject(id);
      notify.success(t('projects.duplicated', 'Projeto duplicado'));
    } catch {
      notify.error(t('errors.saveFailed', 'Falha ao duplicar projeto'));
    }
  };

  const handleCoverStudioConfirm = async (dataUrl: string | undefined) => {
    const projectId = coverStudioProjectId;
    if (!projectId) return;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;

    const loadingId = notify.loading(t('meta.uploadingCover', 'Salvando capa...'));
    try {
      await updateProject(projectId, {
        metadata: { ...project.metadata, coverImage: dataUrl },
      });
      toast.dismiss(loadingId);
      if (dataUrl) {
        // cerimônia: a capa nova entra com flip e brilho
        window.dispatchEvent(new CustomEvent('reformu:moment', {
          detail: { type: 'coverswap', title: project.metadata.title || '', img: dataUrl },
        }));
      } else {
        notify.success(t('meta.coverRemoved', 'Capa removida'));
      }
    } catch {
      toast.dismiss(loadingId);
      notify.error(t('errors.saveFailed', 'Falha ao salvar alterações'));
    } finally {
      setCoverStudioProjectId(null);
    }
  };

  const handleSelectProject = (id: string) => {
    setSelectedProjectId(id);
  };

  const handleDrawerClose = () => {
    setSelectedProjectId(null);
  };

  // ── Shelf grouping ─────────────────────────────────────────────────────────
  const sorted = [...projects].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  const writing = sorted.filter((p) => {
    const prog = deriveProgress(p.chapters);
    return prog > 0 && prog < 100;
  });
  const drafts = sorted.filter((p) => deriveProgress(p.chapters) === 0);
  const done = sorted.filter((p) => deriveProgress(p.chapters) >= 100);

  const shelves: ShelfDef[] = [
    { id: 'writing', label: t('projectsView.shelfWriting', 'Em Escrita'), Icon: PenLine, projects: writing },
    { id: 'drafts', label: t('projectsView.shelfDrafts', 'Rascunhos'), Icon: FileText, projects: drafts },
    { id: 'done', label: t('projectsView.shelfDone', 'Concluídos'), Icon: CheckCircle2, projects: done },
  ];

  // Show at least the first shelf regardless of emptiness
  const visibleShelves = shelves.filter((s, i) => i === 0 || s.projects.length > 0);

  const selectedProject = selectedProjectId
    ? projects.find((p) => p.id === selectedProjectId) ?? null
    : null;

  // Estante mode splits the screen inside 100dvh — no page scroll;
  // grid mode (and the empty state) keeps the normal scrolling page.
  const shelfMode = !projectsLoaded || projects.length > 0;

  return (
    <div
      className={cn(
        'flex-1 [-webkit-overflow-scrolling:touch]',
        shelfMode ? 'min-h-0 overflow-hidden flex flex-col' : 'overflow-y-auto'
      )}
    >
      <div
        className={cn(
          'px-4 md:px-8 w-full',
          shelfMode
            ? 'py-3 md:py-4 flex-1 min-h-0 flex flex-col'
            : 'py-4 md:py-8'
        )}
      >

        {/* ── Header ─────────────────────────────────────────────────────── */}
        {/* ── lib-head do mock: eyebrow + Fraunces + ações fantasma ── */}
        <div className="lib-head">
          <div className="min-w-0">
            <p className="m-eyebrow">
              {t('projectsView.libraryEyebrow', 'Biblioteca')} · {t('estante.shelf', 'Estante')}
            </p>
            <h3>{t('estante.title', 'Seus mundos, lado a lado.')}</h3>
          </div>
          <span className="flex gap-2 shrink-0">
            <button className="btn-ghost-mock hidden sm:inline-flex items-center gap-1.5" onClick={handleGuidedCreate}>
              <Sparkles className="h-3 w-3" />
              {t('projectsView.createWithAi', 'Criar com IA')}
            </button>
            <button className="btn-ghost-mock hidden sm:inline-flex items-center gap-1.5" onClick={handleCreateManga}>
              <BookOpen className="h-3 w-3" />
              {t('projectsView.createManga', 'Mangá / HQ')}
            </button>
            <button className="btn-ghost-mock" onClick={handleNewProject}>
              + {canCreate
                ? t('estante.newJourney', 'Nova jornada')
                : usage
                  ? `${usage.projects.used}/${usage.projects.limit}`
                  : t('estante.newJourney', 'Nova jornada')}
            </button>
          </span>
        </div>

        {/* ── Empty State ────────────────────────────────────────────────── */}
        {!projectsLoaded ? (
          <EstanteSkeleton />
        ) : projects.length === 0 ? (
          <EmptyState
            icon={<BookOpen className="h-9 w-9" />}
            eyebrow={t('projectsView.libraryEyebrow', 'Biblioteca')}
            title={t('projectsView.emptyShelfTitle', 'Sua estante está esperando.')}
            description={t('projectsView.emptyShelfDescription', 'Todo livro começa aqui. Escolha um modelo ou deixe a IA criar a premissa por você.')}
            action={
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <Button
                  onClick={handleGuidedCreate}
                  variant="outline"
                  size="lg"
                  className="border-primary/40 text-primary hover:bg-primary/5 hover:border-primary gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {t('projectsView.createWithAi', 'Criar com IA')}
                </Button>
                <Button
                  onClick={handleNewProject}
                  size="lg"
                  variant={canCreate ? 'default' : 'secondary'}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {canCreate ? t('projectsView.createFirstBook', 'Criar meu primeiro livro') : t('subscription.upgradeNow', 'Fazer upgrade')}
                </Button>
              </div>
            }
            className="py-24"
          />
        ) : (
          /* Só a Estante — o modo grade foi aposentado. min-h-0 + flex column
             deixa o BookshelfView (.msk-shelf-outer, flex:1 justify-center)
             centralizar o bloco da estante VERTICALMENTE na área útil. */
          <div className="flex-1 min-h-0 flex flex-col">
            <BookshelfView />
          </div>
        )}

        {/* Template Modal — call-site preserved exactly */}
        <TemplateModal open={showTemplateModal} onOpenChange={setShowTemplateModal} />

        {/* Book Metadata Modal */}
        {metaModalProject && (
          <BookMetadataModal
            project={metaModalProject}
            open={!!metaModalProject}
            onOpenChange={(open) => { if (!open) setMetaModalProject(null); }}
          />
        )}

        {/* Cover Studio — modal on desktop, drawer on mobile */}
        {(() => {
          const coverProject = coverStudioProjectId
            ? projects.find((p) => p.id === coverStudioProjectId)
            : null;
          return (
            <CoverStudio
              open={!!coverStudioProjectId}
              onOpenChange={(isOpen) => { if (!isOpen) setCoverStudioProjectId(null); }}
              projectId={coverStudioProjectId ?? undefined}
              currentCover={coverProject?.metadata.coverImage}
              bookTitle={coverProject?.metadata.title}
              bookAuthor={coverProject?.metadata.author}
              bookGenre={coverProject?.metadata.genre}
              onConfirm={handleCoverStudioConfirm}
            />
          );
        })()}
      </div>

      {/*
        Book Detail Drawer — vaul handles its own portal and animation.
        Always mounted; open/project props drive visibility.
      */}
      <BookDetailDrawer
        project={selectedProject}
        open={!!selectedProjectId}
        onClose={handleDrawerClose}
        onEditDetails={(p) => {
          handleDrawerClose();
          setMetaModalProject(p);
        }}
        onDuplicate={(id) => {
          handleDuplicateProject(id);
        }}
        onDelete={(id) => {
          handleDeleteProject(id);
        }}
        onChangeCover={(id) => {
          handleDrawerClose();
          setCoverStudioProjectId(id);
        }}
        onShare={(id) => {
          handleDrawerClose();
          setShareProjectId(id);
        }}
      />

      {/* Share Modal */}
      {shareProjectId && (() => {
        const sp = projects.find((p) => p.id === shareProjectId);
        const apiProject = sp as unknown as { isOwner?: boolean; role?: string };
        return (
          <ShareModal
            open={!!shareProjectId}
            onOpenChange={(open) => { if (!open) setShareProjectId(null); }}
            projectId={shareProjectId}
            projectTitle={sp?.metadata.title}
            isOwner={apiProject?.isOwner !== false}
          />
        );
      })()}

      {/* Guided "Create with AI" — modal on desktop, drawer on mobile */}
      <GuidedCreateModal open={guidedOpen} onOpenChange={setGuidedOpen} />

      {/* Manga/comic creation wizard — modal on desktop, drawer on mobile */}
      <MangaCreateWizard
        open={mangaWizardOpen}
        onOpenChange={setMangaWizardOpen}
        onCreated={(id) => navigate(`/app/manga/${id}`)}
      />
    </div>
  );
}

// ─── Shelf ────────────────────────────────────────────────────────────────────

interface ShelfProps {
  shelf: ShelfDef;
  isFirst: boolean;
  selectedProjectId: string | null;
  onSelectProject: (id: string) => void;
  onNewBook: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

function Shelf({
  shelf,
  isFirst,
  selectedProjectId,
  onSelectProject,
  onNewBook,
  t,
}: ShelfProps) {
  const { Icon } = shelf;

  return (
    <div>
      {/* Shelf label */}
      <div className="flex items-center gap-2.5 mb-3 px-1">
        <div className="p-1.5 rounded-full bg-primary/10">
          <Icon className="w-4 h-4 text-primary" />
        </div>
        <h4 className="font-serif font-medium italic text-foreground text-base md:text-lg tracking-tight">
          {shelf.label}
        </h4>
        <span className="text-[10px] font-mono font-semibold text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
          {shelf.projects.length}
          {isFirst ? ` + 1` : ''}
        </span>
      </div>

      {/* Shelf cabinet */}
      <div
        className="relative rounded-2xl pt-8 pb-5 px-5"
        style={{
          background: 'linear-gradient(180deg, var(--color-ink-950)/8 0%, var(--color-primary-950)/5 100%)',
          border: '1px solid var(--border)',
        }}
      >
        {/* Subtle backboard texture */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden opacity-[0.04] pointer-events-none"
          aria-hidden
        >
          <div className="w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(90deg, currentColor 0px, currentColor 1px, transparent 1px, transparent 48px)',
          }} />
        </div>

        {/* Books row */}
        <div
          className={cn(
            'relative flex items-end gap-1.5 md:gap-2.5 pb-3 px-1 min-h-[244px] select-none',
            'overflow-x-auto md:overflow-visible',
            'md:flex-wrap',
          )}
          style={{ scrollbarWidth: 'thin' }}
        >
          {shelf.projects.length === 0 && isFirst ? (
            <div className="flex-1 flex flex-col items-center justify-center py-10 text-center min-h-[160px]">
              <Library className="w-6 h-6 text-muted-foreground/25 mb-2.5" strokeWidth={1} />
              <p className="font-serif text-sm text-muted-foreground/50 italic">
                {t('projectsView.noBooksInProgress', 'Nenhum livro em escrita ainda.')}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/30 mt-1">
                {t('projectsView.addFirstAbove', 'Adicione seu primeiro acima')}
              </p>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {shelf.projects.map((project) => {
                const progress = deriveProgress(project.chapters);
                const title = project.metadata.title || t('projects.untitled');
                const totalWords = project.chapters.reduce((s, c) => s + countWords(c.content), 0);
                const isSelected = selectedProjectId === project.id;

                return (
                  <BookSlot
                    key={project.id}
                    project={project}
                    progress={progress}
                    title={title}
                    totalWords={totalWords}
                    height={deriveHeight(project.id)}
                    isSelected={isSelected}
                    onSelect={onSelectProject}
                    t={t}
                  />
                );
              })}

              {/* Placeholder "Novo Livro" */}
              {isFirst && (
                <motion.div
                  key="placeholder"
                  layout
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.6 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                  className="flex-shrink-0 w-11 md:w-12 h-56"
                >
                  <button
                    onClick={onNewBook}
                    className={cn(
                      'w-full h-full rounded-r-sm flex flex-col items-center justify-center gap-2',
                      'border-2 border-dashed border-border',
                      'bg-muted/10 hover:bg-primary/5 hover:border-primary/40',
                      'text-muted-foreground hover:text-primary',
                      'transition-all duration-300 cursor-pointer',
                    )}
                  >
                    <Plus className="w-4 h-4" />
                    <span
                      className="text-[7px] font-mono tracking-wider uppercase text-center leading-tight"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                    >
                      {t('projectsView.newBook', 'Novo Livro')}
                    </span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>

        {/* Shelf plank (ledge) */}
        <div className="relative mt-0">
          <div
            className="h-3.5 rounded-sm border-t border-border/40"
            style={{
              background:
                'linear-gradient(to bottom, var(--color-primary-800) 0%, var(--color-ink-900) 100%)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.32), 0 2px 4px rgba(0,0,0,0.22)',
            }}
          >
            <div className="h-px w-full bg-white/10 rounded-t-sm" />
          </div>
          <div
            className="h-2 rounded-b-sm opacity-35"
            style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, transparent 100%)' }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── BookSlot ─────────────────────────────────────────────────────────────────

// Spine thickness scales with the book's PAGE COUNT (≈ 275 words/page).
// A short draft stays slim (~34px); a ~350-page book reaches the max (~66px).
function deriveThickness(totalWords: number): number {
  const pages = Math.ceil(totalWords / 275);
  return Math.round(34 + Math.min(pages / 350, 1) * 32);
}

interface BookSlotProps {
  project: Project;
  progress: number;
  title: string;
  totalWords: number;
  height: number;
  isSelected: boolean;
  onSelect: (id: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

function BookSlot({
  project,
  progress,
  title,
  totalWords,
  height,
  isSelected,
  onSelect,
  t,
}: BookSlotProps) {
  const navigate = useNavigate();
  const apiProject = project as unknown as { isOwner?: boolean; role?: string };
  const isOwner = apiProject.isOwner !== false;
  const isSharedWithMe = !isOwner;
  const [isHovered, setIsHovered] = useState(false);
  const thickness = deriveThickness(totalWords);
  // Cover face width derived from height using a ~2:3 book proportion
  const coverFaceWidth = Math.round(height * 0.66);
  // How far each neighbour is pushed when the book opens — symmetric on BOTH
  // sides. Tuned DOWN (×0.45) so it's a tidy nudge, not a huge gap.
  const spread = Math.round((coverFaceWidth - thickness) * 0.45);
  // Shift the opening book left by half the cover overflow so it stays centered
  // in the space it opens into (instead of growing only rightward).
  const openShift = Math.round((coverFaceWidth - thickness) / 2);

  // Derive year from createdAt, fallback to updatedAt
  const year = (project.createdAt instanceof Date
    ? project.createdAt
    : new Date(project.createdAt)
  ).getFullYear() || (project.updatedAt instanceof Date
    ? project.updatedAt
    : new Date(project.updatedAt)
  ).getFullYear();

  const handleClick = () => {
    onSelect(project.id);
  };

  // ── 3D flip angle ──────────────────────────────────────────────────────────
  // The hinge is on the LEFT edge (transform-origin: left center).
  // At rest: inner rotateY(0) → spine faces user, cover hidden behind (rotateY 90deg).
  // On hover: inner rotates so cover faces user.
  // Since spine width = `thickness` and cover width = COVER_FACE_WIDTH,
  // we need the cover face (offset at thickness from origin, then rotated 90deg)
  // to swing forward. The flip angle: -90deg rotates the inner so the cover
  // (which is at 90deg relative to the spine) now faces the viewer.
  // Exactly -90° so the cover sits perfectly flat-front and the spine goes exactly
  // edge-on, flush at the hinge (any other angle leaves a misaligned seam/strip).
  const flipAngle = isHovered ? -90 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 20 }}
      animate={{
        opacity: 1,
        scale: 1,
        y: 0,
        // Symmetric open: on hover, push BOTH neighbours apart equally via margin
        // (keeps the shelf order/flow intact). The cover overflows into the right
        // margin while the inner is shifted left to stay centered.
        marginLeft: isHovered ? spread : 0,
        marginRight: isHovered ? spread : 0,
      }}
      exit={{ opacity: 0, scale: 0.6, y: 16 }}
      transition={{
        default: { type: 'spring', stiffness: 380, damping: 28 },
        marginLeft: { duration: 0.42, ease: [0.25, 0.46, 0.45, 0.94] },
        marginRight: { duration: 0.42, ease: [0.25, 0.46, 0.45, 0.94] },
      }}
      className="group relative flex-shrink-0"
      style={{
        width: thickness,
        height,
        overflow: 'visible',
        zIndex: isHovered ? 30 : undefined,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/*
        Quick action above the spine — the shelf's FAST LANE: jump straight into
        the editor to keep writing. All management actions (cover, share,
        duplicate, delete, edit) live in the detail card that opens on click,
        so the spine stays clean and unambiguous (click = manage, this = write).
      */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            key="toolbar"
            initial={{ opacity: 0, y: 6, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.92 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -top-10 left-1/2 -translate-x-1/2 z-40 whitespace-nowrap pointer-events-auto"
          >
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/app/${(project as { kind?: string }).kind === 'manga' ? 'manga' : 'editor'}/${project.id}`); }}
              title={t('projectsView.openInEditor', 'Abrir no editor')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
                'bg-primary text-primary-foreground',
                'text-[11px] font-semibold tracking-wide',
                'shadow-lg shadow-primary/25 ring-1 ring-primary/30',
                'hover:opacity-95 active:scale-95 transition-all',
              )}
            >
              <PenLine className="w-3 h-3" />
              {t('projectsView.write', 'Escrever')}
            </button>
            {/* Little pointer connecting the pill to the spine */}
            <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 rotate-45 bg-primary" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* "Compartilhado" badge — shown below the book for projects shared with me */}
      {isSharedWithMe && (
        <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-0.5 whitespace-nowrap">
          <Users className="w-2.5 h-2.5 text-primary/70" />
          <span className="text-[8px] font-mono text-primary/70 uppercase tracking-wide">
            {apiProject.role}
          </span>
        </div>
      )}

      {/*
        3D Book Flip Container
        ─────────────────────────────────────────────────────────────────────────
        Perspective wrapper: gives the 3D depth context.
        The inner div rotates around the LEFT edge (transform-origin: left center),
        acting as the hinge between spine and cover.

        Layout inside the inner:
          • Spine face   — position: relative (normal flow), width = thickness
          • Cover face   — position: absolute, left = thickness (right of the spine/hinge)
                           rotated 90deg around the hinge so it's "behind" the spine at rest;
                           when inner flips -88deg the cover swings to face the viewer.
      */}
      <div
        style={{
          perspective: '800px',
          perspectiveOrigin: 'left center',
          width: thickness,
          height,
          position: 'relative',
        }}
        onClick={handleClick}
        title={title}
        className="cursor-pointer"
      >
        {/* Inner — the rotating hinge group */}
        <motion.div
          animate={{ rotateY: flipAngle, x: isHovered ? -openShift : 0 }}
          transition={{ duration: 0.42, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{
            width: thickness,
            height,
            position: 'relative',
            transformOrigin: 'left center',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* ── SPINE FACE (front at rest) ─────────────────────────────── */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
            }}
          >
            <BookCover
              title={title}
              author={project.metadata.author || ''}
              genre={project.metadata.genre || ''}
              coverImage={project.metadata.coverImage}
              variant="spine"
              progress={progress}
              seed={project.id}
              thickness={thickness}
              height={height}
              year={year}
            />
          </div>

          {/* ── COVER FACE (rotated 90deg, lives to the right of the hinge) ─ */}
          {/*
            The cover face is at rotateY(-90deg) relative to the inner wrapper.
            At rest (inner = 0): cover is perpendicular to viewer (hidden side).
            At hover (inner = -88deg): cover swings to face viewer (~-88 + 90 = ~2deg ≈ front).
            translateX(thickness) positions it at the hinge edge.
          */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: coverFaceWidth,
              height,
              transformOrigin: 'left center',
              transform: `translateX(${thickness}px) rotateY(90deg)`,
              transformStyle: 'preserve-3d',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              // Constant subtle drop shadow — no extra shadow appearing on hover.
              filter: 'drop-shadow(-3px 3px 7px rgba(0,0,0,0.26))',
            }}
          >
            <BookCover
              title={title}
              author={project.metadata.author || ''}
              genre={project.metadata.genre || ''}
              coverImage={project.metadata.coverImage}
              variant="cover"
              progress={progress}
              seed={project.id}
              year={year}
              disableTilt
              className="w-full h-full"
            />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

